import { ResultOf } from 'gql.tada';
import { WishlistsFragment } from '@/features/games/internal/api/wishlists/graphql/wishlists.fragment';
import { convertWishlists } from '@/features/games/internal/api/wishlists/convert-wishlists';
import { WishlistData } from '@/features/games/internal/types/wishlists';

export async function fetchWishlists(): Promise<ResultOf<typeof WishlistsFragment> | undefined> {
  const response = await fetch(`/api/game/wishlist`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes
let cache: WishlistData[] | null = null;
let expiresAt = 0;
let inflight: Promise<WishlistData[]> | null = null;

export async function fetchWishlistsCached(forceRefresh = false): Promise<WishlistData[]> {
  const now = Date.now();
  const isValid = !forceRefresh && cache && expiresAt > now;

  if (isValid) {
    return cache!;
  }

  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    try {
      let wishlists: WishlistData[] = [];
      const rawWishlists = await fetchWishlists();
      if (rawWishlists && Object.keys(rawWishlists).length > 0) {
        wishlists = convertWishlists(rawWishlists);
      }
      cache = wishlists;
      expiresAt = Date.now() + TTL_MS;
      return wishlists;
    } catch (err) {
      console.error('Failed to fetch wishlists:', err);
      throw err;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function bustWishlistsCache() {
  cache = null;
  expiresAt = 0;
}
